/* Page-builder blueprints.
   Each section type declares its editable fields; the admin auto-generates the
   editor from this, and the storefront renders by `type` + `content`. */

export const PAGES = [
  { key: "settings", label: "Site settings", types: ["site_settings"] },
  { key: "home", label: "Home", types: ["seo", "home_hero", "home_promos", "home_statement", "home_services", "home_steps", "home_cta", "hero", "banner", "feature_grid", "product_showcase", "image_text", "gallery", "rich_text", "cta", "faq", "home_categories", "home_products", "home_brands", "home_journal"] },
  { key: "services", label: "Services", types: ["seo", "services_hero", "services_main", "services_sectors", "services_cta"] },
  { key: "shop", label: "Shop / Catalogue", types: ["seo"] },
  { key: "blog", label: "Blog / Journal", types: ["seo"] },
  { key: "header", label: "Header", types: ["nav_links", "announcement_bar"] },
  { key: "footer", label: "Footer", types: ["footer_columns", "social_links", "rich_text"] },
  { key: "announcement", label: "Announcement bar", types: ["announcement_bar"] },
  { key: "about", label: "About", types: ["seo", "hero", "rich_text", "image_text", "feature_grid", "gallery", "cta", "about_hero", "about_story", "about_why", "about_stats", "about_engagements", "about_faq", "about_cta"] },
  { key: "contact", label: "Contact", types: ["seo", "contact_info", "image_text", "rich_text", "map", "contact_hero"] },
  { key: "legal", label: "Legal", types: ["legal_mentions", "legal_cgv", "legal_confidentialite"] },
];

const link = (name, label) => ({ name, type: "link", label });
const text = (name, label) => ({ name, type: "text", label });
const area = (name, label) => ({ name, type: "textarea", label });

export const SECTION_TYPES = {
  seo: {
    label: "SEO",
    description: "Search/meta title, description and social image for this page. Leave blank to keep the defaults.",
    fields: [
      text("meta_title", "Meta title"),
      area("meta_description", "Meta description"),
      text("keywords", "Keywords (comma-separated)"),
      { name: "og_image", type: "image", label: "Social share image (OG)" },
      { name: "no_index", type: "select", label: "Indexing", options: [{ value: "", label: "Indexed (default)" }, { value: "1", label: "No-index (hide from search)" }] },
    ],
  },
  site_settings: {
    label: "Site settings",
    description: "Logo, contact, social and navigation — used across the whole site.",
    fields: [
      { name: "logo", type: "image", label: "Logo" },
      { name: "favicon", type: "image", label: "Favicon (browser tab icon)" },
      text("name", "Site name"),
      text("tagline", "Tagline"),
      area("description", "Description (SEO)"),
      area("keywords", "Default keywords (comma-separated)"),
      text("google_site_verification", "Google Search Console verification code"),
      text("phone", "Phone"),
      text("whatsapp", "WhatsApp number"),
      text("email", "Email"),
      text("address", "Address"),
      text("hours", "Opening hours"),
      { name: "social", type: "repeater", label: "Social networks", itemLabel: "Network", itemFields: [text("network", "Network (facebook, instagram, tiktok, linkedin)"), link("url", "URL")] },
      { name: "nav", type: "repeater", label: "Navigation links", itemLabel: "Link", itemFields: [text("label", "Label"), link("url", "URL")] },
    ],
  },
  home_hero: {
    label: "Hero — home slider",
    description: "The rotating hero slides on the home page.",
    fields: [
      { name: "slides", type: "repeater", label: "Slides", itemLabel: "Slide", itemFields: [text("eyebrow", "Eyebrow"), text("lead", "Headline (start)"), text("accent", "Headline (accent word)"), area("sub", "Subtitle"), { name: "image", type: "image", label: "Background image" }] },
      { name: "stats", type: "repeater", label: "Stats", itemLabel: "Stat", itemFields: [text("value", "Value"), text("label", "Label")] },
    ],
  },
  home_promos: {
    label: "Promo banners — home",
    description: "The two promo banners under the products.",
    fields: [
      { name: "items", type: "repeater", label: "Banners", itemLabel: "Banner", itemFields: [text("eyebrow", "Eyebrow"), text("lead", "Title (start)"), text("accent", "Title (accent)"), { name: "image", type: "image", label: "Image" }, text("button_label", "Button label"), link("button_url", "Button link")] },
    ],
  },
  home_statement: {
    label: "Statement — home dark band",
    description: "The dark statement band with stats.",
    fields: [
      text("overline", "Overline"), text("heading", "Heading"), area("text", "Text"), text("button_label", "Button label"), link("button_url", "Button link"),
      { name: "stats", type: "repeater", label: "Stats", itemLabel: "Stat", itemFields: [text("value", "Value (number)"), text("suffix", "Suffix"), text("label", "Label")] },
    ],
  },
  home_services: {
    label: "Services — home",
    description: "The services cards on the home page.",
    fields: [
      text("overline", "Overline"), text("heading", "Heading"), area("sub", "Subtitle"),
      { name: "items", type: "repeater", label: "Services", itemLabel: "Service", itemFields: [text("icon", "Icon name (Headset, FileText, Truck, ShieldCheck, Buildings, Package)"), text("title", "Title"), area("text", "Text")] },
    ],
  },
  home_steps: {
    label: "How it works — home",
    description: "The numbered steps + image block.",
    fields: [
      text("overline", "Overline"), text("heading", "Heading"), area("sub", "Subtitle"), { name: "image", type: "image", label: "Image" }, text("button_label", "Button label"), link("button_url", "Button link"),
      { name: "items", type: "repeater", label: "Steps", itemLabel: "Step", itemFields: [text("title", "Title"), area("text", "Text")] },
    ],
  },
  home_cta: {
    label: "Call to action — home",
    description: "Closing CTA band on the home page.",
    fields: [text("heading", "Heading"), area("text", "Text"), text("button_label", "Button label"), link("button_url", "Button link")],
  },
  hero: {
    label: "Hero",
    description: "Large banner with heading, text and a button.",
    fields: [
      text("heading", "Heading"),
      area("subheading", "Subheading"),
      { name: "image", type: "image", label: "Background image" },
      text("image_alt", "Image alt text"),
      text("button_label", "Button label"),
      link("button_url", "Button link"),
    ],
  },
  banner: {
    label: "Promo banner",
    description: "A compact promotional strip with an optional button.",
    fields: [text("text", "Text"), { name: "image", type: "image", label: "Image" }, text("button_label", "Button label"), link("button_url", "Button link")],
  },
  feature_grid: {
    label: "Feature grid",
    description: "A grid of features / benefits.",
    fields: [
      text("heading", "Heading"),
      {
        name: "items", type: "repeater", label: "Features", itemLabel: "Feature",
        itemFields: [text("icon", "Icon name"), text("title", "Title"), area("text", "Text")],
      },
    ],
  },
  product_showcase: {
    label: "Product showcase",
    description: "Pull in products dynamically.",
    fields: [
      text("heading", "Heading"),
      {
        name: "source", type: "select", label: "Show",
        options: [
          { value: "featured", label: "Featured products" },
          { value: "top_sellers", label: "Top sellers" },
          { value: "special_offers", label: "Special offers" },
        ],
      },
      { name: "limit", type: "number", label: "How many" },
    ],
  },
  image_text: {
    label: "Image + text",
    description: "An image beside a block of text.",
    fields: [
      { name: "image", type: "image", label: "Image" },
      text("image_alt", "Image alt text"),
      text("heading", "Heading"),
      area("body", "Body"),
      text("button_label", "Button label"),
      link("button_url", "Button link"),
      { name: "image_position", type: "select", label: "Image side", options: [{ value: "left", label: "Left" }, { value: "right", label: "Right" }] },
    ],
  },
  gallery: {
    label: "Gallery",
    description: "A set of images.",
    fields: [text("heading", "Heading"), { name: "images", type: "image_list", label: "Images" }],
  },
  rich_text: {
    label: "Rich text",
    description: "A formatted text block.",
    fields: [text("heading", "Heading"), { name: "body", type: "richtext", label: "Body" }],
  },
  cta: {
    label: "Call to action",
    description: "A heading, text and a button.",
    fields: [text("heading", "Heading"), area("text", "Text"), text("button_label", "Button label"), link("button_url", "Button link")],
  },
  faq: {
    label: "FAQ",
    description: "Frequently asked questions.",
    fields: [
      text("heading", "Heading"),
      { name: "items", type: "repeater", label: "Questions", itemLabel: "Q & A", itemFields: [text("question", "Question"), area("answer", "Answer")] },
    ],
  },
  nav_links: {
    label: "Navigation links",
    description: "Top navigation menu items.",
    fields: [{ name: "links", type: "repeater", label: "Links", itemLabel: "Link", itemFields: [text("label", "Label"), link("url", "URL")] }],
  },
  announcement_bar: {
    label: "Announcement bar",
    description: "A site-wide message strip.",
    fields: [text("text", "Message"), text("link_label", "Link label"), link("link_url", "Link URL")],
  },
  footer_columns: {
    label: "Footer columns",
    description: "Columns of links in the footer.",
    fields: [
      {
        name: "columns", type: "repeater", label: "Columns", itemLabel: "Column",
        itemFields: [
          text("title", "Column title"),
          { name: "links", type: "repeater", label: "Links", itemLabel: "Link", itemFields: [text("label", "Label"), link("url", "URL")] },
        ],
      },
    ],
  },
  social_links: {
    label: "Social links",
    description: "Links to your social networks.",
    fields: [{ name: "links", type: "repeater", label: "Networks", itemLabel: "Network", itemFields: [text("network", "Network (facebook, instagram…)"), link("url", "URL")] }],
  },
  contact_info: {
    label: "Contact info",
    description: "Address, phone, email and hours.",
    fields: [area("address", "Address"), text("phone", "Phone"), text("email", "Email"), text("hours", "Opening hours")],
  },
  map: {
    label: "Map",
    description: "An embedded Google Map with a heading and a directions button.",
    fields: [text("overline", "Overline"), text("heading", "Heading"), link("embed_url", "Google Maps embed URL"), text("button_label", "Button label"), link("button_url", "Directions link")],
  },
  legal_mentions: {
    label: "Legal — Mentions légales",
    description: "Overrides the legal notice body (leave empty to keep the built-in text).",
    fields: [text("title", "Title"), text("updated", "Last updated"), { name: "body", type: "richtext", label: "Body" }],
  },
  legal_cgv: {
    label: "Legal — Conditions générales",
    description: "Overrides the T&C body (leave empty to keep the built-in text).",
    fields: [text("title", "Title"), text("updated", "Last updated"), { name: "body", type: "richtext", label: "Body" }],
  },
  legal_confidentialite: {
    label: "Legal — Confidentialité",
    description: "Overrides the privacy policy body (leave empty to keep the built-in text).",
    fields: [text("title", "Title"), text("updated", "Last updated"), { name: "body", type: "richtext", label: "Body" }],
  },
  about_hero: {
    label: "About — Hero",
    description: "The hero band at the top of the About page.",
    fields: [
      text("overline", "Overline"),
      text("heading", "Heading"),
      area("subtitle", "Subtitle"),
      { name: "background_image", type: "image", label: "Background image" },
    ],
  },
  about_story: {
    label: "About — Story & Milestones",
    description: "The store intro block and the milestones timeline.",
    fields: [
      { name: "image", type: "image", label: "Store image" },
      text("badge", "Image badge (store · address)"),
      text("overline", "Overline"),
      text("title", "Title"),
      area("text", "Intro text"),
      { name: "features", type: "repeater", label: "Checklist features", itemLabel: "Feature", itemFields: [text("text", "Text")] },
      text("story_overline", "Timeline overline"),
      text("story_heading", "Timeline heading"),
      { name: "milestones", type: "repeater", label: "Milestones (timeline)", itemLabel: "Milestone", itemFields: [text("year", "Year"), text("title", "Title"), area("text", "Text")] },
    ],
  },
  about_why: {
    label: "About — Why Choose Us",
    description: "The 'why choose us' cards.",
    fields: [
      text("overline", "Overline"),
      text("heading", "Heading"),
      { name: "items", type: "repeater", label: "Cards", itemLabel: "Card", itemFields: [{ name: "icon", type: "select", label: "Icon (ShieldCheck, Truck, Headset, Wrench, Certificate, Package, FileText)" }, text("title", "Title"), area("text", "Text")] },
    ],
  },
  about_stats: {
    label: "About — Stats",
    description: "The animated stats row on the About page.",
    fields: [
      { name: "stats", type: "repeater", label: "Stats", itemLabel: "Stat", itemFields: [text("value", "Value (number)"), text("suffix", "Suffix (+, h, %, …)"), text("label", "Label")] },
    ],
  },
  about_engagements: {
    label: "About — Engagements",
    description: "The commitments block on the About page.",
    fields: [
      text("overline", "Overline"),
      text("heading", "Heading"),
      area("sub", "Subtitle"),
      { name: "items", type: "repeater", label: "Engagements", itemLabel: "Engagement", itemFields: [{ name: "icon", type: "select", label: "Icon (ShieldCheck, Truck, Headset, Wrench, Certificate, Package, FileText)" }, text("title", "Title"), area("text", "Text")] },
    ],
  },
  about_faq: {
    label: "About — FAQ",
    description: "Frequently asked questions on the About page.",
    fields: [
      text("overline", "Overline"),
      text("heading", "Heading"),
      area("sub", "Subtitle"),
      { name: "items", type: "repeater", label: "FAQ items", itemLabel: "Question", itemFields: [text("q", "Question"), area("a", "Answer")] },
    ],
  },
  about_cta: {
    label: "About — CTA",
    description: "Closing CTA band on the About page.",
    fields: [text("heading", "Heading"), area("text", "Text"), text("button_label", "Button label"), link("button_url", "Button URL")],
  },
  services_hero: {
    label: "Hero — services",
    description: "The dark hero band at the top of the Services page (phone comes from Site settings).",
    fields: [
      text("breadcrumb", "Breadcrumb label"),
      text("heading", "Heading"),
      area("sub", "Subtitle"),
      text("button_label", "Button label"),
      link("button_url", "Button link"),
    ],
  },
  services_main: {
    label: "Services list — services page",
    description: "The grid of service cards on the Services page.",
    fields: [
      { name: "items", type: "repeater", label: "Services", itemLabel: "Service", itemFields: [text("icon", "Icon name (Headset, FileText, Truck, ShieldCheck, Buildings, Package)"), text("title", "Title"), area("text", "Text")] },
    ],
  },
  services_sectors: {
    label: "Sectors — services page",
    description: "The 'sectors we serve' grid with overline, heading and icon cards.",
    fields: [
      text("overline", "Overline"),
      text("heading", "Heading"),
      area("sub", "Subtitle"),
      { name: "items", type: "repeater", label: "Sectors", itemLabel: "Sector", itemFields: [text("icon", "Icon name (Buildings, Factory, Wrench, Car, Plant, Hammer)"), text("name", "Name")] },
    ],
  },
  services_cta: {
    label: "Call to action — services page",
    description: "Closing CTA band with two buttons on the Services page.",
    fields: [
      text("heading", "Heading"),
      area("text", "Text"),
      text("button_label", "Primary button label"),
      link("button_url", "Primary button link"),
      text("button2_label", "Secondary button label"),
      link("button2_url", "Secondary button link"),
    ],
  },
  contact_hero: {
    label: "Hero — contact",
    description: "The hero band and form heading on the Contact page.",
    fields: [
      text("heading", "Heading"),
      area("subtitle", "Subtitle"),
      text("form_heading", "Form heading"),
    ],
  },
  home_categories: {
    label: "Categories — home heading",
    description: "Overline and heading for the categories section on the home page.",
    fields: [text("overline", "Overline"), text("heading", "Heading")],
  },
  home_products: {
    label: "Products — home heading",
    description: "Overline and heading for the products section on the home page.",
    fields: [text("overline", "Overline"), text("heading", "Heading")],
  },
  home_brands: {
    label: "Brands — home heading",
    description: "Overline and heading for the brands section on the home page.",
    fields: [text("overline", "Overline"), text("heading", "Heading")],
  },
  home_journal: {
    label: "Journal — home heading",
    description: "Overline and heading for the journal section on the home page.",
    fields: [text("overline", "Overline"), text("heading", "Heading")],
  },
};

export const typeLabel = (type) => SECTION_TYPES[type]?.label || type;
